import { useMemo, useState } from "react";
import { useNotification } from "~/hooks";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetInventories } from "~/services/inventory";
import { useGetAllStaff } from "~/services/operation/operation.queries";
import { useUpdateServiceTicket } from "~/services/service-ticket";
import { useGetServiceTickets } from "~/services/service-ticket/service-ticket.queries";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { TInventory, TServiceTicket, TStaff } from "~/types";
import { StaffAvailabilitySchedule } from "~/types/staff-availability-schedule";
import { SortOrder, StaffAvailability, StaffStatus } from "~/utils/constants";
import {
  ContentPanel,
  HeaderComponent,
  InventoryList,
  ServiceSelector,
  StaffList,
  TabSelector,
} from "./components";

export const QuickAdd = () => {
  // Tab state
  const [tab, setTab] = useState<"staff" | "inventory">("staff");
  const [selectedDate, setSelectedDate] = useState<string>("2025-03-07");

  // Search states
  const [searchStaffTerm, setSearchStaffTerm] = useState<string | undefined>();
  const [searchServiceTerm, setSearchServiceTerm] = useState<
    string | undefined
  >();
  const [searchInventoryTerm, setSearchInventoryTerm] = useState<
    string | undefined
  >();

  // Debounced search values
  const [searchStaff] = useDebounceValue(searchStaffTerm, 500);
  const [searchService] = useDebounceValue(searchServiceTerm, 500);
  const [searchInventory] = useDebounceValue(searchInventoryTerm, 500);

  // Pagination states
  const [servicePage, setServicePage] = useState<number>(1);
  const [staffPage, setStaffPage] = useState<number>(1);
  const [inventoryPage, setInventoryPage] = useState<number>(1);

  // Notifications
  const { success, error } = useNotification();

  // Selected items
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(
    null
  );

  // Data storage
  const [services, setServices] = useState<TServiceTicket[]>([]);
  const [staffs, setStaffs] = useState<TStaff[]>([]);
  const [inventories, setInventories] = useState<TInventory[]>([]);

  // Changes tracking
  const [staffIdsToAdd, setStaffIdsToAdd] = useState<string[]>([]);
  const [staffIdsToDelete, setStaffIdsToDelete] = useState<string[]>([]);

  // Staff filters - default to hiding pending and unavailable
  const [hidePending, setHidePending] = useState<boolean>(true);
  const [hideUnavailable, setHideUnavailable] = useState<boolean>(true);
  const [inventoryIdsToAdd, setInventoryIdsToAdd] = useState<string[]>([]);
  const [inventoryIdsToDelete, setInventoryIdsToDelete] = useState<string[]>(
    []
  );

  const PAGE_SIZE = 6;

  // Query parameter memos
  const filterParamsStaffs = useMemo(
    () => ({
      page: staffPage,
      pageSize: PAGE_SIZE,
      search: searchStaff,
    }),
    [searchStaff, staffPage]
  );

  const filterParamsInventories = useMemo(
    () => ({
      page: inventoryPage,
      pageSize: PAGE_SIZE,
      search: searchInventory,
    }),
    [searchInventory, inventoryPage]
  );

  const filterParamsService = useMemo(() => {
    const now = new Date();
    const tenDaysFromNow = new Date();
    tenDaysFromNow.setDate(now.getDate() + 10);

    return {
      page: servicePage,
      pageSize: PAGE_SIZE,
      search: searchService,
      startDate: now.toISOString(),
      endDate: tenDaysFromNow.toISOString(),
    };
  }, [searchService, servicePage]);

  // Data fetching
  const {
    data: serviceData,
    isLoading: isLoadingService,
    refetch: refetchServices,
  } = useGetServiceTickets({
    ...filterParamsService,
  });

  const {
    data: staffsData,
    isLoading: isLoadingStaff,
    refetch: refetchStaffs,
  } = useGetAllStaff({
    sortByCreatedAt: SortOrder.DESC,
    ...filterParamsStaffs,
  });

  const {
    data: inventoriesData,
    isLoading: isLoadingInventory,
    refetch: refetchInventories,
  } = useGetInventories(
    {
      sortByCreatedAt: SortOrder.DESC,
      ...filterParamsInventories,
      clientId: selectedService?.jobTicket?.client?.id, // This ensures only client-tagged inventory is shown
    },
    {
      enabled: !!selectedService && !!selectedService?.jobTicket?.client?.id, // Only fetch when we have a valid client ID
    }
  );

  // Get the service date for availability filtering
  const serviceDate = useMemo(() => {
    if (!selectedService?.serviceDatetime) return null;
    const date = new Date(selectedService.serviceDatetime);
    // Format as YYYY-MM-DD for the API
    return date.toISOString().split('T')[0];
  }, [selectedService?.serviceDatetime]);

  // Fetch staff availability for the selected service date
  const {
    data: availabilityData,
    isLoading: isLoadingAvailability,
  } = useGetStaffAvailabilities(
    {
      startDate: serviceDate ? `${serviceDate}T00:00:00.000Z` : undefined,
      endDate: serviceDate ? `${serviceDate}T23:59:59.999Z` : undefined,
      pageSize: 500, // Get all availability records for the date
    },
    !!serviceDate // Only fetch when we have a service date
  );

  // Create a map of staff availability for quick lookup
  const staffAvailabilityMap = useMemo(() => {
    const map = new Map<string, StaffAvailabilitySchedule>();
    if (availabilityData?.items) {
      availabilityData.items.forEach((schedule) => {
        map.set(schedule.staffId, schedule);
      });
    }
    return map;
  }, [availabilityData]);

  // Enhance staff list with availability info - show active staff, sorted by availability
  const availableStaffs = useMemo(() => {
    // Filter out INACTIVE and HIATUS staff
    let filteredStaffs = staffs.filter((staff) =>
      staff.status !== StaffStatus.INACTIVE &&
      staff.status !== StaffStatus.HIATUS
    );

    // Apply additional filters when a service is selected
    if (serviceDate) {
      filteredStaffs = filteredStaffs.filter((staff) => {
        const availability = staffAvailabilityMap.get(staff.id);
        const isAlreadyAssigned = selectedService?.staffs?.some((s) => s.id === staff.id);

        // Always show assigned staff regardless of filters
        if (isAlreadyAssigned) return true;

        // Hide pending (no availability record) if filter is on
        if (hidePending && !availability) return false;

        // Hide unavailable if filter is on
        if (hideUnavailable && availability?.availability === StaffAvailability.UNAVAILABLE) {
          return false;
        }

        return true;
      });
    }

    if (!serviceDate || !staffsData?.items) {
      // No service selected, show all active staff
      return filteredStaffs;
    }

    // Sort staff by availability status:
    // 1. Staff already assigned to this service (top)
    // 2. Staff with FULL_TIME/PART_TIME availability
    // 3. Staff with no availability record (pending)
    // 4. Staff marked as UNAVAILABLE (bottom)
    return [...filteredStaffs].sort((a, b) => {
      const availA = staffAvailabilityMap.get(a.id);
      const availB = staffAvailabilityMap.get(b.id);
      const assignedA = selectedService?.staffs?.some((s) => s.id === a.id) ? 1 : 0;
      const assignedB = selectedService?.staffs?.some((s) => s.id === b.id) ? 1 : 0;

      // Already assigned staff first
      if (assignedA !== assignedB) return assignedB - assignedA;

      // Then by availability status
      const getAvailPriority = (avail: typeof availA) => {
        if (!avail) return 2; // Pending (no record)
        if (avail.availability === StaffAvailability.FULL_TIME) return 0;
        if (avail.availability === StaffAvailability.PART_TIME) return 1;
        if (avail.availability === StaffAvailability.UNAVAILABLE) return 3;
        return 2;
      };

      return getAvailPriority(availA) - getAvailPriority(availB);
    });
  }, [staffs, serviceDate, staffAvailabilityMap, selectedService?.staffs, staffsData?.items, hidePending, hideUnavailable]);

  // Update local state when data changes - sort by serviceDatetime as failsafe
  useMemo(() => {
    if (serviceData?.items) {
      const sortedItems = [...serviceData.items].sort((a, b) => {
        const dateA = a.serviceDatetime ? new Date(a.serviceDatetime).getTime() : 0;
        const dateB = b.serviceDatetime ? new Date(b.serviceDatetime).getTime() : 0;
        return dateA - dateB; // Ascending chronological order
      });

      setServices((prev) => {
        if (servicePage === 1) {
          return sortedItems;
        }
        // Merge and re-sort to maintain chronological order across pages
        const merged = [...prev, ...sortedItems];
        return merged.sort((a, b) => {
          const dateA = a.serviceDatetime ? new Date(a.serviceDatetime).getTime() : 0;
          const dateB = b.serviceDatetime ? new Date(b.serviceDatetime).getTime() : 0;
          return dateA - dateB;
        });
      });
    }
  }, [serviceData, servicePage]);

  useMemo(() => {
    if (staffsData?.items) {
      setStaffs((prev) =>
        staffPage === 1 ? staffsData.items : [...prev, ...staffsData.items]
      );
    }
  }, [staffsData, staffPage]);

  useMemo(() => {
    if (inventoriesData?.items) {
      setInventories((prev) =>
        inventoryPage === 1
          ? inventoriesData.items
          : [...prev, ...inventoriesData.items]
      );
    }
  }, [inventoriesData, inventoryPage]);

  // Pagination helpers
  const hasMoreServices = (): boolean =>
    !!serviceData?.meta.totalItems &&
    services.length < serviceData.meta.totalItems;

  const hasMoreStaffs = (): boolean =>
    !!staffsData?.meta.totalItems && staffs.length < staffsData.meta.totalItems;

  const hasMoreInventories = (): boolean =>
    !!inventoriesData?.meta.totalItems &&
    inventories.length < inventoriesData.meta.totalItems;

  const loadMoreServices = (): void => {
    if (hasMoreServices()) {
      setServicePage((prev) => prev + 1);
    }
  };

  const loadMoreStaffs = (): void => {
    if (hasMoreStaffs()) {
      setStaffPage((prev) => prev + 1);
    }
  };

  const loadMoreInventories = (): void => {
    if (hasMoreInventories()) {
      setInventoryPage((prev) => prev + 1);
    }
  };

  // Staff management handlers
  const handleAddStaff = (staffId: string) => {
    setStaffIdsToAdd((prev) => [...prev, staffId]);
    setStaffIdsToDelete((prev) => prev.filter((id) => id !== staffId));
  };

  const handleRemoveStaff = (staffId: string) => {
    setStaffIdsToAdd((prev) => prev.filter((id) => id !== staffId));
    if (selectedService?.staffs?.some((s) => s.id === staffId)) {
      setStaffIdsToDelete((prev) => [...prev, staffId]);
    }
  };

  // Inventory management handlers
  const handleAddInventory = (inventoryId: string) => {
    setInventoryIdsToAdd((prev) => [...prev, inventoryId]);
    setInventoryIdsToDelete((prev) => prev.filter((id) => id !== inventoryId));
  };

  const handleRemoveInventory = (inventoryId: string) => {
    setInventoryIdsToAdd((prev) => prev.filter((id) => id !== inventoryId));
    if (selectedService?.inventories?.some((inv) => inv.id === inventoryId)) {
      setInventoryIdsToDelete((prev) => [...prev, inventoryId]);
    }
  };

  // Service selection
  const handleSelectService = (service: TServiceTicket) => {
    setSelectedService(service);
    setStaffIdsToAdd([]);
    setStaffIdsToDelete([]);
    setInventoryIdsToAdd([]);
    setInventoryIdsToDelete([]);

    // Force refresh inventory data for new client
    setInventoryPage(1);
    setInventories([]);
  };

  // State checks
  const isStaffAssigned = (staffId: string): boolean => {
    return (
      staffIdsToAdd.includes(staffId) ||
      (!!selectedService?.staffs?.some((s) => s.id === staffId) &&
        !staffIdsToDelete.includes(staffId))
    );
  };

  const isInventoryAssigned = (inventoryId: string): boolean => {
    return (
      inventoryIdsToAdd.includes(inventoryId) ||
      (!!selectedService?.inventories?.some((inv) => inv.id === inventoryId) &&
        !inventoryIdsToDelete.includes(inventoryId))
    );
  };

  // Reset all states
  const handleReset = () => {
    setServicePage(1);
    setStaffPage(1);
    setInventoryPage(1);
    setSearchServiceTerm(undefined);
    setSearchStaffTerm(undefined);
    setSearchInventoryTerm(undefined);
    setSelectedService(null);
    setStaffIdsToAdd([]);
    setStaffIdsToDelete([]);
    setInventoryIdsToAdd([]);
    setInventoryIdsToDelete([]);
    setServices([]);
    setStaffs([]);
    setInventories([]);
    refetchServices();
    refetchStaffs();
  };

  // Search handlers
  const handleStaffSearch = (term: string) => {
    setSearchStaffTerm(term);
    setStaffPage(1);
    setStaffs([]);
  };

  const handleInventorySearch = (term: string) => {
    setSearchInventoryTerm(term);
    setInventoryPage(1);
    setInventories([]);
  };

  // const handleServiceSearch = (term: string) => {
  //   setSearchServiceTerm(term);
  //   setServicePage(1);
  //   setServices([]);
  // };

  // Save changes
  const mutationUpdate = useUpdateServiceTicket();

  const handleSave = () => {
    if (!selectedService) return;

    const updateData = {
      id: selectedService.id,
      staffIds: [
        ...(selectedService.staffs?.map((s) => s.id) || []).filter(
          (id) => !staffIdsToDelete.includes(id)
        ),
        ...staffIdsToAdd,
      ],
      staffIdsToDelete,
      inventoryIds: [
        ...(selectedService.inventories?.map((inv) => inv.id) || []).filter(
          (id) => !inventoryIdsToDelete.includes(id)
        ),
        ...inventoryIdsToAdd,
      ],
      inventoryIdsToDelete,
    };

    mutationUpdate.mutate(updateData, {
      onSuccess: () => {
        success({
          message: "Service updated successfully",
          description: "Service has been updated successfully.",
        });
        // Refresh data after successful update
        refetchServices();
        refetchStaffs();
        refetchInventories();

        // Clear the changes tracking
        setStaffIdsToAdd([]);
        setStaffIdsToDelete([]);
        setInventoryIdsToAdd([]);
        setInventoryIdsToDelete([]);
      },
      onError: (err) => {
        console.error("Error updating service:", err);
        error({
          message: "Error updating service",
          description:
            "There was an error updating the service. Please try again.",
        });
      },
    });
  };

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <HeaderComponent
        handleReset={handleReset}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      {/* Tabs */}
      <TabSelector activeTab={tab} setActiveTab={setTab} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Service Selection */}
        <div className="lg:col-span-1">
          <ServiceSelector
            services={services}
            isLoading={isLoadingService}
            selectedService={selectedService}
            onSelectService={handleSelectService}
            loadMore={loadMoreServices}
            hasMore={hasMoreServices()}
          />
        </div>

        {/* Right Column - Staff or Inventory */}
        <div className="lg:col-span-2">
          <ContentPanel
            title={tab === "staff" ? "Staff Members" : "Inventory Items"}
            selectedServiceLabel={selectedService?.serial}
            onSave={handleSave}
            isSaving={mutationUpdate.isPending}
          >
            {tab === "staff" ? (
              <StaffList
                staffs={availableStaffs}
                isLoading={isLoadingStaff || isLoadingAvailability}
                onAddStaff={handleAddStaff}
                onRemoveStaff={handleRemoveStaff}
                isStaffAssigned={isStaffAssigned}
                hasServiceSelected={!!selectedService}
                loadMore={loadMoreStaffs}
                hasMore={hasMoreStaffs()}
                onSearch={handleStaffSearch}
                staffAvailabilityMap={staffAvailabilityMap}
                serviceDate={serviceDate}
                hidePending={hidePending}
                hideUnavailable={hideUnavailable}
                onToggleHidePending={setHidePending}
                onToggleHideUnavailable={setHideUnavailable}
              />
            ) : (
              <InventoryList
                inventories={inventories}
                isLoading={isLoadingInventory}
                onAddInventory={handleAddInventory}
                onRemoveInventory={handleRemoveInventory}
                isInventoryAssigned={isInventoryAssigned}
                hasServiceSelected={!!selectedService}
                loadMore={loadMoreInventories}
                hasMore={hasMoreInventories()}
                onSearch={handleInventorySearch}
                selectedClientId={selectedService?.jobTicket?.client?.id}
              />
            )}
          </ContentPanel>
        </div>
      </div>
    </div>
  );
};
